// useOverlays — the viz-owned Mind Map overlay (staged merges / reassignments) for a project,
// stored via /api/synthesis-viz/:projectId/overlays. Per the spec a merge is a STAGED EDIT written
// to this overlay — deliberately SEPARATE from the DTSE entity_registry — so hand-curation survives
// a re-run and never writes back into the engine. The actual frequency/severity RECOMPUTE is
// server-side (it lands with the engine wiring); the component never does that math, so merged
// counts render "pending" until the recompute returns. With no projectId the overlay is local-only.

import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../api';

export interface OverlayEntry {
  entity_id: string;
  overlay: Record<string, any>;
}

export function useOverlays(projectId?: string) {
  const [overlays, setOverlays] = useState<OverlayEntry[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!projectId) { setOverlays([]); setLoaded(true); return; }
    let alive = true;
    setLoaded(false);
    (async () => {
      try {
        const res = await apiClient.get(`/api/synthesis-viz/${encodeURIComponent(projectId)}/overlays`);
        const items = res?.data?.items;
        if (alive) setOverlays(Array.isArray(items) ? items : []);
      } catch {
        if (alive) setOverlays([]);       // no route / no rows — empty, never an error surface
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, [projectId]);

  const saveOverlay = useCallback(async (entityId: string, overlay: Record<string, any>) => {
    if (!projectId) return false;         // nothing to persist against; caller keeps the local edit
    try {
      await apiClient.put(
        `/api/synthesis-viz/${encodeURIComponent(projectId)}/overlays/${encodeURIComponent(entityId)}`,
        { overlay },
      );
      return true;
    } catch {
      return false;
    }
  }, [projectId]);

  // No DELETE on the route; a removed overlay is a tombstone the loader ignores.
  const removeOverlay = useCallback(
    (entityId: string) => saveOverlay(entityId, { type: 'removed' }),
    [saveOverlay],
  );

  return { overlays, loaded, editable: !!projectId, saveOverlay, removeOverlay };
}
